import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALL_REGIONS = [
    { code: "IN", name: "India", currency: "INR", symbol: "₹", flag: "🇮🇳", rebate: true },
    { code: "AU", name: "Australia", currency: "USD", symbol: "$", flag: "🇦🇺", rebate: false },
    { code: "AE", name: "United Arab Emirates", currency: "USD", symbol: "$", flag: "🇦🇪", rebate: false },
    { code: "GB", name: "United Kingdom", currency: "USD", symbol: "$", flag: "🇬🇧", rebate: false },
    { code: "US", name: "United States", currency: "USD", symbol: "$", flag: "🇺🇸", rebate: false },
    { code: "CA", name: "Canada", currency: "USD", symbol: "$", flag: "🇨🇦", rebate: false },
    { code: "SG", name: "Singapore", currency: "USD", symbol: "$", flag: "🇸🇬", rebate: false },
    { code: "NZ", name: "New Zealand", currency: "USD", symbol: "$", flag: "🇳🇿", rebate: false },
    { code: "GLOBAL", name: "Global", currency: "USD", symbol: "$", flag: "🌐", rebate: false },
];

const DEFAULT_REGION = ALL_REGIONS.find((r) => r.code === "GLOBAL");

const RegionContext = createContext({
    region: DEFAULT_REGION,
    regions: ALL_REGIONS,
    setRegion: () => {},
    loading: true,
    detectedCountry: null,
});

export const RegionProvider = ({ children }) => {
    const [region, setRegionState] = useState(DEFAULT_REGION);
    const [loading, setLoading] = useState(true);
    const [detectedCountry, setDetectedCountry] = useState(null);

    useEffect(() => {
        const stored = typeof window !== "undefined" ? window.localStorage.getItem("indoblitz.region") : null;
        if (stored) {
            const found = ALL_REGIONS.find((r) => r.code === stored);
            if (found) {
                setRegionState(found);
                setLoading(false);
                return;
            }
        }
        // Auto-detect via backend
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API}/geo`, { timeout: 6000 });
                if (cancelled) return;
                const code = res.data?.region?.code;
                setDetectedCountry(res.data?.detected_country || null);
                const found = ALL_REGIONS.find((r) => r.code === code);
                if (found) setRegionState(found);
            } catch (e) {
                // keep default
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const setRegion = useCallback((code) => {
        const found = ALL_REGIONS.find((r) => r.code === code);
        if (found) {
            setRegionState(found);
            try { window.localStorage.setItem("indoblitz.region", code); } catch (e) {}
        }
    }, []);

    const value = useMemo(
        () => ({ region, regions: ALL_REGIONS, setRegion, loading, detectedCountry }),
        [region, setRegion, loading, detectedCountry]
    );

    return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
};

export const useRegion = () => useContext(RegionContext);

export const isIndia = (region) => region?.code === "IN";
